import {
  mapRegExp,
  tinyassert,
  typedBoolean
} from "./chunk-6QQNBPBT.js";

// src/react-router.ts
import internalEager from "virtual:@hiogawa/vite-glob-routes/internal/page-routes";
import internalLazy from "virtual:@hiogawa/vite-glob-routes/internal/page-routes/lazy";

// src/react-router-utils.ts
function createGlobPageRoutes(internal) {
  const mapping = createGlobPageMapping(internal);
  const routes = createGlobPageRoutesInner(internal.eager, mapping);
  return { routes };
}
function createGlobPageMapping(internal) {
  const patterns = [
    [internal.globPage, /^(.*)\.page\./, false],
    [internal.globPageServer, /^(.*)\.page\.server\./, true],
    [internal.globLayout, /^(.*)layout\./, false],
    [internal.globLayoutServer, /^(.*)layout\.server\./, true]
  ];
  const result = {};
  for (const [glob, regex, isServer] of patterns) {
    for (const [file, mod] of Object.entries(glob)) {
      const urlpath = file.slice(internal.root.length).match(regex)[1];
      (result[urlpath] ??= []).push({ file, mod, isServer });
    }
  }
  return result;
}
function createGlobPageRoutesInner(eager, mapping) {
  const pathEntries = Object.entries(mapping).map(([k, v]) => ({
    keys: splitPathSegment(k),
    value: v
  }));
  const tree = createTree(pathEntries);
  function recurse(children) {
    return Object.entries(children).map(([path, node]) => {
      const route = {
        path: formatPath(path)
      };
      if (node.value) {
        const entries = node.value;
        route.globInfo = { entries };
        if (eager) {
          const mods = entries.map((e) => {
            tinyassert(typeof e.mod !== "function");
            return e.mod;
          });
          Object.assign(route, ...mods);
        } else {
          route.lazy = async () => {
            const mods = await Promise.all(
              entries.map((e) => {
                tinyassert(typeof e.mod === "function");
                return e.mod();
              })
            );
            return Object.assign({}, ...mods);
          };
        }
      }
      if (node.children) {
        route.children = recurse(node.children);
      }
      if (path === "index") {
        route.index = true;
        delete route.path;
        delete route.children;
      }
      return route;
    });
  }
  return tree.children ? recurse(tree.children) : [];
}
function initNode() {
  return {};
}
function createTree(entries) {
  const root = initNode();
  for (const e of entries) {
    let node = root;
    for (const key of e.keys) {
      node = (node.children ??= {})[key] ??= initNode();
    }
    node.value = e.value;
  }
  return root;
}
function splitPathSegment(pathname) {
  const result = [];
  mapRegExp(
    pathname,
    /([^/]*\/)/g,
    (match) => {
      tinyassert(1 in match);
      result.push(match[1]);
    },
    (nonMatch) => {
      result.push(nonMatch);
    }
  );
  return result;
}
function formatPath(s) {
  const m = s.match(/^\[(.*)\](\/?)$/);
  if (m) {
    return ":" + m[1] + (m[2] ?? "");
  }
  return s;
}

// src/react-router-helper-server.ts
import {
  isRouteErrorResponse,
  matchRoutes
} from "react-router";
import {
  createStaticHandler,
  createStaticRouter
} from "react-router-dom/server";
async function handleReactRouterServer({
  routes,
  request,
  requestContext
}) {
  const handler = createStaticHandler(routes);
  const context = await handler.query(request, { requestContext });
  if (context instanceof Response) {
    return {
      type: "response",
      response: context
    };
  }
  return {
    type: "render",
    context,
    router: createStaticRouter(handler.dataRoutes, context),
    statusCode: getResponseStatusCode(context)
  };
}
function getResponseStatusCode(context) {
  if (context.errors) {
    const errorResponses = Object.values(context.errors).map((e) => isRouteErrorResponse(e) && e).filter(typedBoolean);
    if (errorResponses.length) {
      return Math.max(...errorResponses.map((e) => e.status));
    }
    return 500;
  }
  return 200;
}
function getCurrentRouteAssets({
  context,
  routes,
  manifest
}) {
  const matchedRoutes = matchRoutes(routes, context.location) ?? [];
  const files = matchedRoutes.flatMap((m) => m.route.globInfo?.entries.map((e) => !e.isServer && e.file)).filter(typedBoolean);
  if (manifest) {
    return resolveManifestAssets(files, manifest);
  }
  return files;
}
function resolveManifestAssets(files, manifest) {
  const entryKeys = /* @__PURE__ */ new Set();
  function collectEnryKeysRecursive(key) {
    if (!entryKeys.has(key)) {
      const e = manifest[key];
      tinyassert(e);
      entryKeys.add(key);
      for (const nextKey of e.imports ?? []) {
        collectEnryKeysRecursive(nextKey);
      }
    }
  }
  for (const file of files) {
    collectEnryKeysRecursive(file.slice(1));
  }
  return [...entryKeys].map((key) => "/" + manifest[key].file);
}

// src/react-router-helper-client.ts
import "react-router";
import { createBrowserRouter } from "react-router-dom";
async function initializeReactRouterClient({
  routes
}) {
  const router = createBrowserRouter(routes);
  await routerInitializedPromise(router);
  return { router };
}
async function routerInitializedPromise(router) {
  if (router.state.initialized) {
    return;
  }
  await new Promise((resolve) => {
    const unsubscribe = router.subscribe((state) => {
      if (state.initialized) {
        resolve();
        unsubscribe();
      }
    });
  });
}

// src/react-router.ts
function globPageRoutes() {
  return createGlobPageRoutes(internalEager);
}
function globPageRoutesLazy() {
  return createGlobPageRoutes(internalLazy);
}
export {
  getCurrentRouteAssets,
  globPageRoutes,
  globPageRoutesLazy,
  handleReactRouterServer,
  initializeReactRouterClient
};
